import * as Sentry from "@sentry/browser";
import {
  pushAnalyticsEvent,
  pushVariablesToDataLayer,
} from "@ottatech/analytics";
import { useQuery } from "@apollo/client";
import { useEffect } from "react";

import { CurrentUserDocument, CurrentUserQuery } from "../../schema";

type User = NonNullable<CurrentUserQuery["currentUser"]>;

const configureSentry = (user: User): void => {
  if (
    typeof Sentry !== "undefined" &&
    typeof Sentry?.configureScope === "function"
  ) {
    Sentry.configureScope(scope => {
      scope.setUser({ email: user.email });
    });
  }
};

const getUserProperties = (user: User) => {
  const functions = user.jobFunctionPreferences.map(pref => pref.functionId);
  const subFunctions = user.jobSubFunctionPreferences.map(
    pref => pref.subFunctionId
  );
  const locationPreferences = user.locationPreferences.map(
    pref => pref.location
  );
  const companySizes = user.companySizePreferences.map(
    pref => pref.companySizeId
  );

  return {
    role: user.role,
    insertedAt: user.insertedAt,
    company: user.currentCompany?.id,
    proTier: user.currentCompany?.proTier,
    functions,
    subFunctions,
    locationPreferences,
    companySizes,
    showProfileToCompanies: user.showProfileToCompanies,
    profileCompletion: user.profileCompletion,
    searchStage: user.searchStage,
    signupRegion: user.signupRegion,
    numberJobsSeen: user.jobReactionStatistics.numberJobsSeen,
    numberJobsSaved: user.jobReactionStatistics.numberJobsSaved,
  };
};

const configureAmplitude = (user: User): void => {
  if (typeof window?.amplitude?.getInstance === "function") {
    const instance = window.amplitude.getInstance();
    instance.setUserId(user.id);
    instance.setUserProperties(getUserProperties(user));
  }
};

export function Analytics(): null {
  const { data } = useQuery(CurrentUserDocument);

  useEffect(() => {
    if (process.env.NODE_ENV === "production" && data?.currentUser?.email) {
      const user = data?.currentUser;
      configureSentry(user);
      configureAmplitude(user);
      pushVariablesToDataLayer({
        userId: user.id,
        signupRegion: user.signupRegion,
      });
    }
    if (data?.currentUser?.retained === true) {
      pushAnalyticsEvent({ eventName: "Candidate Retained" });
    }
  }, [data?.currentUser]);

  return null;
}
